import { state } from './store'

const AI_AGENT_KEYS = {
  agent1_sst: 'boston_ai_agent1_sst_v1',
  agent2_ssc: 'boston_ai_agent2_ssc_v1',
  agent3_gen: 'boston_ai_agent3_gen_v4',
  agent4_scriptlogic: 'boston_ai_agent4_scriptlogic_v1',
}

try { ['v1', 'v2', 'v3', 'v4', 'v5', 'v6'].forEach((v) => localStorage.removeItem('boston_ai_prompt_' + v)) } catch (_) {}
try { ['v1', 'v2', 'v3'].forEach((v) => localStorage.removeItem('boston_ai_agent3_gen_' + v)) } catch (_) {}
try { ['v0'].forEach((v) => localStorage.removeItem('boston_ai_agent1_sst_' + v)) } catch (_) {}
try { ['v0'].forEach((v) => localStorage.removeItem('boston_ai_agent2_ssc_' + v)) } catch (_) {}

const AI_CONFIG_KEY = 'boston_ai_config_v1'

const AI_AGENT_PROMPT_URLS = {
  agent1_sst: 'api:/admin/ai?resource=prompts&action=active&agent=agent1_sst',
  agent2_ssc: 'api:/admin/ai?resource=prompts&action=active&agent=agent2_ssc',
  agent3_gen: 'api:/admin/ai?resource=prompts&action=active&agent=agent3_gen',
  agent4_scriptlogic: 'api:/admin/ai?resource=prompts&action=active&agent=agent4_scriptlogic',
}

const AI_AGENT_DEFAULTS = { agent1_sst: '', agent2_ssc: '', agent3_gen: '', agent4_scriptlogic: '' }

const AI_PRICING = {
  'claude-opus-5': { in: 15.00, out: 75.00 },
  'claude-opus-4-8': { in: 15.00, out: 75.00 },
  'claude-opus-4-6': { in: 15.00, out: 75.00 },
  'claude-sonnet-4-5': { in: 3.00, out: 15.00 },
  'claude-haiku-4-5-20251001': { in: 1.00, out: 5.00 },
}

const AI_USAGE_KEY = 'boston_ai_usage_v1'

/* Contexto de costo: identifica QUE script y QUE corrida generan cada llamada.
   Las vistas lo setean al cargar un archivo (aiSetCostContext) y los wrappers
   lo adjuntan a cada request; el BACKEND registra tokens exactos + USD en la
   tabla ai_script_costs (fuente de verdad; localStorage queda como estimado local). */
let _costContext = { scriptUuid: '', scriptName: '', runId: '' }
function aiNewRunId() {
  return 'run_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8)
}
function aiSetCostContext(ctx = {}) {
  _costContext = {
    scriptUuid: ctx.scriptUuid || '',
    scriptName: ctx.scriptName || '',
    runId: ctx.runId || aiNewRunId(),
  }
  return _costContext
}
function aiCostContext(options = {}) {
  return {
    scriptUuid: options.scriptUuid || _costContext.scriptUuid || undefined,
    scriptName: options.scriptName || _costContext.scriptName || undefined,
    runId: options.runId || _costContext.runId || undefined,
  }
}
const IMPORTER_KNOWLEDGE_LIMIT = 5
const IMPORTER_KNOWLEDGE_MAX_CHARS = 12000
const DASHBOARD_KNOWLEDGE_LIMIT = 30
const DASHBOARD_KNOWLEDGE_MAX_CHARS = 40000 // guardrail; el backend igual capa a 30-40k. Baja el techo si crece la KB.

async function aiLoadAgentDefault(agent, forceRefresh) {
  if (!forceRefresh && AI_AGENT_DEFAULTS[agent]) return AI_AGENT_DEFAULTS[agent]
  try {
    const { biaGetActivePrompt } = await import('./biaApi')
    const prompt = await biaGetActivePrompt(agent)
    if (!prompt) {
      console.warn('[aiLoadAgentDefault]', agent, 'sin prompt activo en /admin/ai')
      return ''
    }
    AI_AGENT_DEFAULTS[agent] = prompt
    console.log('[aiLoadAgentDefault]', agent, 'desde /admin/ai prompts active:', prompt.length, 'bytes')
    return prompt
  } catch (error) {
    console.error('[aiLoadAgentDefault]', agent, 'API failed:', error)
    return ''
  }
}

function aiLoadAgentPrompt(agent) {
  try {
    return localStorage.getItem(AI_AGENT_KEYS[agent]) || ''
  } catch (_) {
    return ''
  }
}

function aiSaveAgentPrompt(agent, text) {
  try { localStorage.setItem(AI_AGENT_KEYS[agent], text || '') } catch (_) {}
}

async function aiResolveAgentPrompt(agent) {
  const userEdited = aiLoadAgentPrompt(agent)
  if (userEdited) {
    console.log('[aiResolveAgentPrompt]', agent, 'using USER-EDITED prompt from localStorage (' + userEdited.length + ' bytes)')
    return userEdited
  }
  const fresh = await aiLoadAgentDefault(agent, true)
  console.log('[aiResolveAgentPrompt]', agent, 'using ACTIVE prompt from API (' + fresh.length + ' bytes)')
  return fresh
}

function aiParseTags(row) {
  const raw = row?.tags
  if (!raw) return {}
  if (typeof raw === 'object') return raw
  try { return JSON.parse(raw) } catch (_) {}
  return { tags: String(raw).split(',').map((x) => x.trim()).filter(Boolean) }
}

function aiKnowledgeApplies(row, agent) {
  const tags = aiParseTags(row)
  const agents = Array.isArray(tags.agents) ? tags.agents : []
  return !agents.length || agents.includes(agent)
}

function aiKnowledgePriority(row) {
  const tags = aiParseTags(row)
  return tags.priority || 'normal'
}

function aiKnowledgeTagForAgent(agent) {
  if (agent === 'agent1_sst') return 'sst'
  if (agent === 'agent2_ssc') return 'ssc'
  if (agent === 'agent3_gen') return 'data_json'
  if (agent === 'agent4_scriptlogic') return 'logic'
  return ''
}

async function aiLoadKnowledgeBase(agent, options = {}) {
  let queryDisablesKnowledge = false
  try {
    queryDisablesKnowledge = new URLSearchParams(window.location.search).get('useKnowledge') === 'false'
  } catch (_) {}
  if (options.useKnowledge === false || state.ai.useKnowledge === false || queryDisablesKnowledge) {
    console.log('[aiLoadKnowledgeBase] omitida por useKnowledge=false para', agent)
    return ''
  }
  const started = Date.now()
  try {
    const { biaListKnowledge } = await import('./biaApi')
    const tag = options.tag || aiKnowledgeTagForAgent(agent)
    let rows = await biaListKnowledge({ active: true, agent, tag, limit: options.limit || 60 })
    rows = rows
      .filter((row) => Number(row.status ?? 1) === 1)
      .filter((row) => aiKnowledgeApplies(row, agent))
      .slice(0, options.limit || 60)

    rows.sort((a, b) => ((aiKnowledgePriority(b) === 'high') - (aiKnowledgePriority(a) === 'high')))

    let kb = ''
    let contentChars = 0
    const maxKnowledgeChars = options.maxChars || 24000
    rows.forEach((row) => {
      const title = row.title || row.uuid || 'knowledge'
      const category = row.category || 'general'
      const content = row.content || ''
      if (!content) return
      if (contentChars >= maxKnowledgeChars) return
      const remaining = maxKnowledgeChars - contentChars
      const clipped = content.length > remaining ? content.slice(0, remaining) : content
      contentChars += clipped.length
      kb += '\n\n---\n\n## KNOWLEDGE: ' + title + ' [' + category + ']\n' + clipped + '\n'
    })

    try {
      const lecs = (typeof window !== 'undefined' && window.lecQueue) ? window.lecQueue.approved().slice(-30) : []
      if (lecs.length) {
        kb += '\n\n---\n\n## LECCIONES APRENDIDAS (aprobadas por humanos)\n'
        lecs.forEach((x) => {
          kb += '- [' + (x.script || 'script') + '] "' + String(x.instruccion || '').slice(0, 200) + '" -> ' + String(x.resolucion || '').slice(0, 250) + '\n'
        })
      }
    } catch (error) {
      console.warn('[aiLoadKnowledgeBase] lecciones:', error)
    }

    console.log('[aiLoadKnowledgeBase] agent', agent, 'tag', tag, 'rows', rows.length, 'contentChars', contentChars, 'bytes', kb.length, 'elapsedMs', Date.now() - started)
    return kb
  } catch (error) {
    console.warn('[aiLoadKnowledgeBase] error:', error)
    return ''
  }
}

function aiStripMarkdownJSONFence(text) {
  return String(text || '').replace(/```json/gi, '').replace(/```/g, '').trim()
}

function aiFindJSONObject(text) {
  const source = aiStripMarkdownJSONFence(text)
  try {
    return JSON.parse(source)
  } catch (_) {}

  for (let start = 0; start < source.length; start += 1) {
    if (source[start] !== '{') continue
    let depth = 0
    let inString = false
    let escape = false
    const limit = Math.min(source.length, start + 80000)
    for (let end = start; end < limit; end += 1) {
      const ch = source[end]
      if (escape) {
        escape = false
        continue
      }
      if (ch === '\\' && inString) {
        escape = true
        continue
      }
      if (ch === '"') {
        inString = !inString
        continue
      }
      if (inString) continue
      if (ch === '{') depth += 1
      if (ch === '}') depth -= 1
      if (depth === 0) {
        try {
          return JSON.parse(source.slice(start, end + 1))
        } catch (_) {
          break
        }
      }
    }
  }

  return null
}

function aiFindJSONObjects(text) {
  const source = aiStripMarkdownJSONFence(text)
  const results = []
  try {
    const parsed = JSON.parse(source)
    if (parsed && typeof parsed === 'object') results.push(parsed)
  } catch (_) {}

  for (let start = 0; start < source.length; start += 1) {
    if (source[start] !== '{') continue
    let depth = 0
    let inString = false
    let escape = false
    const limit = Math.min(source.length, start + 80000)
    for (let end = start; end < limit; end += 1) {
      const ch = source[end]
      if (escape) {
        escape = false
        continue
      }
      if (ch === '\\' && inString) {
        escape = true
        continue
      }
      if (ch === '"') {
        inString = !inString
        continue
      }
      if (inString) continue
      if (ch === '{') depth += 1
      if (ch === '}') depth -= 1
      if (depth === 0) {
        try {
          const parsed = JSON.parse(source.slice(start, end + 1))
          if (parsed && typeof parsed === 'object') results.push(parsed)
        } catch (_) {}
        break
      }
    }
  }
  return results
}

function aiIsPlainObject(value) {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function aiValidImporterSummary(summary) {
  const value = String(summary || '').trim()
  if (!value) return false
  const normalized = value.toLowerCase()
  if (normalized === '(el intérprete no devolvió resumen)') return false
  if (normalized === '(el intÃ©rprete no devolviÃ³ resumen)') return false
  if (normalized === 'no disponible') return false
  if (normalized === 'no se pudo determinar') return false
  if (/^<[^>]+>$/.test(value)) return false
  return true
}

function aiImporterIRSummary(ir) {
  const metaSummary = aiIsPlainObject(ir?.meta) ? ir.meta.summary : ''
  const rootSummary = ir?.summary
  if (aiValidImporterSummary(metaSummary)) return String(metaSummary).trim()
  if (aiValidImporterSummary(rootSummary)) return String(rootSummary).trim()
  return ''
}

function aiLooksLikeIR(value) {
  if (!aiIsPlainObject(value)) return false
  return Boolean(
    value.ir_version ||
    value.source ||
    value.fields ||
    value.steps ||
    value.subflows ||
    value.command_plan ||
    value.menu_options ||
    value.branches ||
    value.meta,
  )
}

function aiNormalizeIR(ir) {
  if (!aiIsPlainObject(ir)) return null
  ir.meta = aiIsPlainObject(ir.meta) ? ir.meta : {}
  const summary = aiImporterIRSummary(ir)
  if (summary) {
    ir.meta.summary = summary
    ir.summary = summary
  }
  ir.warnings = Array.isArray(ir.warnings) ? ir.warnings : []
  ir.open_questions = Array.isArray(ir.open_questions) ? ir.open_questions : []
  ir.diagnostics = Array.isArray(ir.diagnostics) ? ir.diagnostics : []
  return ir
}

function isFunctionalImporterIR(ir) {
  if (!aiIsPlainObject(ir)) return false
  const hasStructure = Boolean(
    ir.ir_version ||
    ir.source ||
    ir.fields ||
    ir.steps ||
    ir.subflows ||
    ir.command_plan ||
    ir.menu_options ||
    ir.branches,
  )
  return hasStructure && Boolean(aiImporterIRSummary(ir))
}

function aiNormalizeImporterJSONPayload(value, raw = '', depth = 0) {
  if (depth > 6 || value === undefined || value === null) {
    return depth === 0 && raw ? aiNormalizeImporterJSONPayload(raw, '', depth + 1) : null
  }

  if (typeof value === 'string') {
    const candidates = aiFindJSONObjects(value)
    for (const candidate of candidates) {
      const normalized = aiNormalizeImporterJSONPayload(candidate, '', depth + 1)
      if (isFunctionalImporterIR(normalized)) return normalized
    }
    for (const candidate of candidates) {
      const normalized = aiNormalizeImporterJSONPayload(candidate, '', depth + 1)
      if (normalized) return normalized
    }
    return null
  }

  if (aiLooksLikeIR(value)) {
    return aiNormalizeIR(value)
  }

  if (aiIsPlainObject(value)) {
    const keys = ['data', 'ir', 'result', 'json', 'output', 'content', 'text', 'raw']
    for (const key of keys) {
      if (!(key in value)) continue
      const normalized = aiNormalizeImporterJSONPayload(value[key], '', depth + 1)
      if (isFunctionalImporterIR(normalized)) return normalized
    }
    for (const key of keys) {
      if (!(key in value)) continue
      const normalized = aiNormalizeImporterJSONPayload(value[key], '', depth + 1)
      if (normalized) return normalized
    }
  }

  return depth === 0 && raw ? aiNormalizeImporterJSONPayload(raw, '', depth + 1) : null
}

function aiBuildImporterIRFailure(response, raw, model, usage) {
  const normalized = aiNormalizeImporterJSONPayload(response?.data, raw)
  return {
    ok: false,
    data: {
      error: response?.error || 'Agente 1 no devolvió IR funcional con meta.summary',
      rawExcerpt: raw.slice(0, 1500),
      raw,
      normalized,
    },
    model,
    usage,
    raw,
  }
}

function aiIsAgent3DataJsonAgent(agent) {
  return String(agent || '').trim() === 'agent3_gen' || String(agent || '').trim() === 'boston_ai_agent3_gen_v4'
}

function aiIsAgent4LogicJsonAgent(agent) {
  return String(agent || '').trim() === 'agent4_scriptlogic' || String(agent || '').trim() === 'boston_ai_agent4_scriptlogic_v1'
}

function aiBuildImporterDataJsonResponse(response, raw, model, usage) {
  if (!response?.ok || !response?.data || typeof response.data !== 'object' || Array.isArray(response.data)) {
    return {
      ok: false,
      data: {
        error: response?.error || 'Agente 3 no devolvió data.json válido',
        rawExcerpt: raw.slice(0, 1500),
        raw,
      },
      model,
      usage,
      raw,
    }
  }
  return {
    ok: true,
    data: {
      data: response.data,
      warnings: Array.isArray(response.data.warnings) ? response.data.warnings : [],
    },
    model,
    usage,
    raw,
  }
}

function aiBuildImporterLogicJsonResponse(response, raw, model, usage) {
  if (!response?.ok || !response?.data || typeof response.data !== 'object' || Array.isArray(response.data)) {
    return {
      ok: false,
      data: {
        error: response?.error || 'Agente 4 no devolvió logic.json válido',
        rawExcerpt: raw.slice(0, 1500),
        raw,
      },
      model,
      usage,
      raw,
    }
  }
  return {
    ok: true,
    data: {
      data: response.data,
      warnings: Array.isArray(response.data.warnings) ? response.data.warnings : [],
    },
    model,
    usage,
    raw,
  }
}

function aiCurrentLang(options) {
  try {
    const l = (options && options.lang) || window.localStorage.getItem('boston_dashboard_lang') || 'es'
    return String(l).toLowerCase().indexOf('en') === 0 ? 'en' : 'es'
  } catch (_) { return 'es' }
}

async function aiCallAgent(agent, userPrompt, expectJson = true, options = {}) {
  const knowledgeBase = await aiLoadKnowledgeBase(agent, options)
  const { biaGenerateAssistant } = await import('./biaApi')
  console.log('[ai import] generate agent', agent)
  console.log('[ai import] input length', String(userPrompt || '').length)
  const response = await biaGenerateAssistant({
    agent,
    generationMode: options.generationMode || 'natural',
    input: userPrompt,
    language: aiCurrentLang(options),
    ...aiCostContext(options),
    context: {
      expectJson,
      knowledgeBase,
    },
    createdUser: 'admin_v2',
  })

  const raw = response?.content || ''
  const usage = response?.usage
    ? {
      input_tokens: response.usage.inputTokens || response.usage.input_tokens || 0,
      output_tokens: response.usage.outputTokens || response.usage.output_tokens || 0,
    }
    : null

  if (!expectJson) {
    return { ok: Boolean(response?.ok), data: { data: raw, warnings: [] }, model: response?.model, usage, raw }
  }

  const parsed = aiFindJSONObject(raw)
  if (!response?.ok || !parsed) {
    return {
      ok: false,
      data: {
        error: !response?.ok ? 'La generacion AI no devolvio una respuesta valida' : 'No se pudo extraer JSON valido de la respuesta',
        rawExcerpt: raw.slice(0, 1500),
        raw,
      },
      model: response?.model,
      usage,
      raw,
    }
  }

  return {
    ok: true,
    data: { data: parsed, warnings: [] },
    model: response?.model,
    usage,
    raw,
  }
}

async function aiCallImporterAgent(agent, userPrompt, expectJson = true, options = {}) {
  const { biaGenerateImporterAssistant } = await import('./biaApi')
  console.log('[ai import] importer-assistant agent', agent)
  console.log('[ai import] input length', String(userPrompt || '').length)
  const response = await biaGenerateImporterAssistant({
    agent,
    generationMode: options.generationMode || 'import',
    referenceIr: options.referenceIr || null,
    userPrompt,
    language: aiCurrentLang(options),
    ...aiCostContext(options),
    systemPrompt: options.systemPrompt || '',
    expectJson,
    model: options.model || state.ai.model || '',
    maxTokens: options.maxTokens || 12000,
    temperature: options.temperature ?? 0,
    useKnowledge: options.useKnowledge !== false,
    knowledgeLimit: options.knowledgeLimit || IMPORTER_KNOWLEDGE_LIMIT,
    knowledgeMaxChars: options.knowledgeMaxChars || IMPORTER_KNOWLEDGE_MAX_CHARS,
    createdUser: 'admin_v2',
  })

  const raw = response?.raw || ''
  const usage = response?.usage
    ? {
      input_tokens: response.usage.inputTokens || response.usage.input_tokens || 0,
      output_tokens: response.usage.outputTokens || response.usage.output_tokens || 0,
    }
    : null

  if (!expectJson) {
    return {
      ok: Boolean(response?.ok),
      data: { data: response?.text || response?.data || raw, warnings: [] },
      model: response?.model,
      usage,
      raw,
    }
  }
  if (aiIsAgent3DataJsonAgent(agent)) {
    return aiBuildImporterDataJsonResponse(response, raw, response?.model, usage)
  }
  if (aiIsAgent4LogicJsonAgent(agent)) {
    return aiBuildImporterLogicJsonResponse(response, raw, response?.model, usage)
  }

  const normalized = aiNormalizeImporterJSONPayload(response?.data, raw)
  if (!response?.ok || !isFunctionalImporterIR(normalized)) {
    return aiBuildImporterIRFailure(response, raw, response?.model, usage)
  }

  return {
    ok: true,
    data: { data: normalized, warnings: normalized.warnings || [] },
    model: response.model,
    usage,
    raw,
  }
}

/* Polling del job async de IA (backend devuelve {jobId, status: pending} y el
   resultado queda en ai_jobs). Para resource=assistant el status va por POST con
   source=dashboard + token (así pasa el auth de dashboard sin JWT). */
const DASHBOARD_JOB_POLL_INTERVAL_MS = 2500
const DASHBOARD_IMPORTER_JOB_TIMEOUT_MS = 8 * 60 * 1000

function aiSleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function aiPollDashboardJobStatus({ baseUrl, resource, jobId, token, apiKey, timeoutMs, signal }) {
  const deadline = Date.now() + (timeoutMs || DASHBOARD_IMPORTER_JOB_TIMEOUT_MS)
  const isAssistant = resource === 'assistant'
  const url = `${baseUrl}/admin/ai?resource=${encodeURIComponent(resource)}&action=status&id=${encodeURIComponent(jobId)}`
  while (Date.now() < deadline) {
    await aiSleep(DASHBOARD_JOB_POLL_INTERVAL_MS)
    if (signal && signal.aborted) {
      throw new Error('Proceso detenido por el usuario.')
    }
    const response = await fetch(url, {
      method: isAssistant ? 'POST' : 'GET',
      signal,
      headers: {
        Accept: 'application/json, text/plain, */*',
        ...(isAssistant ? { 'Content-Type': 'application/json' } : {}),
        token,
        'x-api-key': apiKey || '',
      },
      ...(isAssistant ? { body: JSON.stringify({ source: 'dashboard', token, jobId }) } : {}),
    })
    const text = await response.text()
    let payload = null
    try {
      payload = text ? JSON.parse(text) : null
    } catch (_) {
      payload = { message: text }
    }
    if (!response.ok) {
      throw new Error(payload?.message || payload?.error || `No se pudo consultar el estado de la generación (${response.status}).`)
    }
    const status = String(payload?.status || '').toLowerCase()
    if (status === 'error') {
      throw new Error(payload?.error || 'La generación IA falló.')
    }
    if (status === 'done') {
      return payload?.result
    }
  }
  throw new Error('La generación IA superó el tiempo de espera. Reintentá.')
}

async function aiCallAgentDashboard(agent, userPrompt, expectJson = true, options = {}) {
  // Pattern async: kickoff -> polling. El backend ahora encola en ai_jobs y responde
  // rapido con { jobId, status: "pending" } (evita 504 del APIGW a los 29s). El
  // shape final devuelto es identico al POST sincrono anterior.
  const baseUrl = String(options.apiBaseUrl || state.config?.publicApiBaseUrl || state.config?.apiBaseUrl || '').replace(/\/$/, '')
  const sabreToken = String(options.token || '').trim()
  if (!baseUrl) throw new Error('Falta URL publica para generar con IA desde Dashboard.')
  if (!sabreToken) throw new Error('Falta token Sabre para generar con IA desde Dashboard.')

  const commonHeaders = {
    Accept: 'application/json, text/plain, */*',
    'Content-Type': 'application/json',
    token: sabreToken,
    'x-api-key': options.apiKey || '',
  }

  const totalTimeoutMs = options.timeoutMs || 5 * 60 * 1000
  const pollIntervalMs = options.pollIntervalMs || 2000
  const startedAt = Date.now()

  // 1) Kickoff
  const kickoffResp = await fetch(`${baseUrl}/admin/ai?resource=assistant&action=generate`, {
    method: 'POST',
    headers: commonHeaders,
    body: JSON.stringify({
      source: 'dashboard',
      token: sabreToken,
      agent,
      generationMode: options.generationMode || 'natural',
      model: options.model || '',
      input: userPrompt,
      description: userPrompt,
      language: aiCurrentLang(options),
    ...aiCostContext(options),
      useKnowledge: options.useKnowledge !== false,
      knowledgeLimit: options.knowledgeLimit || DASHBOARD_KNOWLEDGE_LIMIT,
      knowledgeMaxChars: options.knowledgeMaxChars || DASHBOARD_KNOWLEDGE_MAX_CHARS,
      context: {
        expectJson,
      },
      createdUser: 'dashboard',
    }),
  })
  const kickoffText = await kickoffResp.text()
  let kickoffData = null
  try {
    kickoffData = kickoffText ? JSON.parse(kickoffText) : null
  } catch (_) {
    kickoffData = { message: kickoffText }
  }
  if (!kickoffResp.ok) {
    throw new Error(kickoffData?.message || kickoffData?.error || `No se pudo generar con IA (${kickoffResp.status}).`)
  }

  let payload = kickoffData
  const jobId = kickoffData?.jobId || kickoffData?.data?.jobId
  if (jobId) {
    // 2) Polling
    console.log('[dashboard assistant] jobId=', jobId, 'polling every', pollIntervalMs, 'ms')
    while (true) {
      if (Date.now() - startedAt > totalTimeoutMs) {
        throw new Error(`Timeout (${Math.round(totalTimeoutMs / 1000)}s) esperando la generacion.`)
      }
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs))

      const statusResp = await fetch(`${baseUrl}/admin/ai?resource=assistant&action=status&id=${encodeURIComponent(jobId)}`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({ source: 'dashboard', token: sabreToken, jobId }),
      })
      const statusText = await statusResp.text()
      let statusData = null
      try {
        statusData = statusText ? JSON.parse(statusText) : null
      } catch (_) {
        statusData = { message: statusText }
      }
      if (!statusResp.ok) {
        throw new Error(statusData?.message || statusData?.error || `No se pudo consultar status (${statusResp.status}).`)
      }

      const status = statusData?.status || statusData?.data?.status
      if (status === 'error') {
        const message = statusData?.error || statusData?.data?.error || 'La generacion fallo en el backend.'
        throw new Error(message)
      }
      if (status === 'done') {
        const result = statusData?.result ?? statusData?.data?.result
        if (result === undefined || result === null) throw new Error('Job completado sin resultado.')
        payload = result
        break
      }
      // status pending -> loop
    }
  }
  // Si NO habia jobId, payload = kickoffData directo (retrocompat con backend sincrono).

  const raw = payload?.content || ''
  const usage = payload?.usage
    ? {
      input_tokens: payload.usage.inputTokens || payload.usage.input_tokens || 0,
      output_tokens: payload.usage.outputTokens || payload.usage.output_tokens || 0,
    }
    : null

  if (!expectJson) {
    return {
      ok: Boolean(payload?.ok),
      data: { data: raw, warnings: [] },
      model: payload?.model,
      usage,
      raw,
      knowledgeRows: payload?.knowledgeRows || 0,
      knowledgeChars: payload?.knowledgeChars || 0,
    }
  }

  const parsed = aiFindJSONObject(raw)
  if (!payload?.ok || !parsed) {
    return {
      ok: false,
      data: {
        error: !payload?.ok ? 'La generacion AI no devolvio una respuesta valida' : 'No se pudo extraer JSON valido de la respuesta',
        rawExcerpt: raw.slice(0, 1500),
        raw,
      },
      model: payload?.model,
      usage,
      raw,
      knowledgeRows: payload?.knowledgeRows || 0,
      knowledgeChars: payload?.knowledgeChars || 0,
    }
  }

  return {
    ok: true,
    data: { data: parsed, warnings: [] },
    model: payload?.model,
    usage,
    raw,
    knowledgeRows: payload?.knowledgeRows || 0,
    knowledgeChars: payload?.knowledgeChars || 0,
  }
}
// biaGenerateDashboardImporterAssistant
//
// Pattern async: en vez de un unico POST bloqueante que supera 29s (techo APIGW
// REST -> 504), el backend crea un job y devuelve un jobId al toque; el front
// pollea status hasta done|error. El shape final devuelto sigue siendo IDENTICO
// al que retornaba el POST sincrono, para que aiCallDashboardImporterAgent y el
// resto de la UI funcionen sin cambios.
async function biaGenerateDashboardImporterAssistant(payload, sabreToken, options = {}) {
  const baseUrl = String(options.apiBaseUrl || state.config?.publicApiBaseUrl || state.config?.apiBaseUrl || '').replace(/\/$/, '')
  const cleanToken = String(sabreToken || '').trim()
  if (!baseUrl) throw new Error('Falta URL publica para generar desde archivo en Dashboard.')
  if (!cleanToken) throw new Error('Falta token Sabre para generar desde archivo en Dashboard.')

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), options.timeoutMs || DASHBOARD_IMPORTER_JOB_TIMEOUT_MS)
  if (options.signal) {
    if (options.signal.aborted) controller.abort()
    else options.signal.addEventListener('abort', () => controller.abort(), { once: true })
  }
  try {
    const response = await fetch(`${baseUrl}/admin/ai?resource=dashboard-importer-assistant&action=generate`, {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        token: cleanToken,
        'x-api-key': options.apiKey || '',
      },
      signal: controller.signal,
      body: JSON.stringify({
        source: 'dashboard_file_import',
        token: cleanToken,
        agent: payload.agent,
        generationMode: payload.generationMode || 'import',
        referenceIr: payload.referenceIr || undefined,
        userPrompt: payload.userPrompt,
        language: payload.language,
        systemPrompt: payload.systemPrompt || '',
        expectJson: payload.expectJson !== false,
        model: payload.model || '',
        maxTokens: payload.maxTokens || 12000,
        useKnowledge: payload.useKnowledge !== false,
        knowledgeLimit: payload.knowledgeLimit || DASHBOARD_KNOWLEDGE_LIMIT,
        knowledgeMaxChars: payload.knowledgeMaxChars || DASHBOARD_KNOWLEDGE_MAX_CHARS,
        createdUser: payload.createdUser || 'dashboard',
      }),
    })

    const text = await response.text()
    let data = null
    try {
      data = text ? JSON.parse(text) : null
    } catch (_) {
      data = { message: text }
    }
    if (!response.ok) {
      throw new Error(data?.message || data?.error || `No se pudo generar desde archivo (${response.status}).`)
    }

    const jobId = data?.jobId || data?.data?.jobId
    if (jobId) {
      const startedAt = Date.now()
      const pollIntervalMs = options.pollIntervalMs || 2000
      while (true) {
        if (Date.now() - startedAt > (options.timeoutMs || DASHBOARD_IMPORTER_JOB_TIMEOUT_MS)) {
          throw new Error('Timeout esperando la generacion desde archivo.')
        }
        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs))
        const statusResponse = await fetch(`${baseUrl}/admin/ai?resource=dashboard-importer-assistant&action=status&id=${encodeURIComponent(jobId)}`, {
          method: 'POST',
          headers: {
            Accept: 'application/json, text/plain, */*',
            'Content-Type': 'application/json',
            token: cleanToken,
            'x-api-key': options.apiKey || '',
          },
          signal: controller.signal,
          body: JSON.stringify({ source: 'dashboard_file_import', token: cleanToken, jobId }),
        })
        const statusText = await statusResponse.text()
        let statusData = null
        try {
          statusData = statusText ? JSON.parse(statusText) : null
        } catch (_) {
          statusData = { message: statusText }
        }
        if (!statusResponse.ok) {
          throw new Error(statusData?.message || statusData?.error || `No se pudo consultar status (${statusResponse.status}).`)
        }
        const status = statusData?.status || statusData?.data?.status
        const result = statusData?.result ?? statusData?.data?.result
        if (status === 'error') {
          if (result !== undefined && result !== null) {
            return {
              ...result,
              ok: false,
              error: result?.error || statusData?.error || statusData?.data?.error || 'La generacion desde archivo fallo en el backend.',
            }
          }
          throw new Error(statusData?.error || statusData?.data?.error || 'La generacion desde archivo fallo en el backend.')
        }
        if (status === 'done') {
          if (result === undefined || result === null) throw new Error('Job completado sin resultado.')
          return result
        }
      }
    }

    return data
  } finally {
    clearTimeout(timer)
  }
}
async function aiCallDashboardImporterAgent(agent, userPrompt, expectJson = true, options = {}) {
  console.log('[dashboard file import] importer-assistant agent', agent)
  console.log('[dashboard file import] input length', String(userPrompt || '').length)
  const response = await biaGenerateDashboardImporterAssistant({
    agent,
    generationMode: options.generationMode || 'import',
    referenceIr: options.referenceIr || null,
    userPrompt,
    language: aiCurrentLang(options),
    ...aiCostContext(options),
    systemPrompt: options.systemPrompt || '',
    expectJson,
    model: options.model || '',
    maxTokens: options.maxTokens || 12000,
    useKnowledge: options.useKnowledge !== false,
    knowledgeLimit: options.knowledgeLimit || DASHBOARD_KNOWLEDGE_LIMIT,
    knowledgeMaxChars: options.knowledgeMaxChars || DASHBOARD_KNOWLEDGE_MAX_CHARS,
    createdUser: 'dashboard',
  }, options.token, options)

  const raw = response?.raw || ''
  const usage = response?.usage
    ? {
      input_tokens: response.usage.inputTokens || response.usage.input_tokens || 0,
      output_tokens: response.usage.outputTokens || response.usage.output_tokens || 0,
    }
    : null

  if (!expectJson) {
    return { ok: Boolean(response?.ok), data: { data: response?.text || response?.data || raw, warnings: [] }, model: response?.model, usage, raw }
  }
  if (aiIsAgent3DataJsonAgent(agent)) {
    return aiBuildImporterDataJsonResponse(response, raw, response?.model, usage)
  }
  if (aiIsAgent4LogicJsonAgent(agent)) {
    return aiBuildImporterLogicJsonResponse(response, raw, response?.model, usage)
  }

  const normalized = aiNormalizeImporterJSONPayload(response?.data, raw)
  if (!response?.ok || !isFunctionalImporterIR(normalized)) {
    return aiBuildImporterIRFailure(response, raw, response?.model, usage)
  }

  return { ok: true, data: { data: normalized, warnings: normalized.warnings || [] }, model: response.model, usage, raw }
}

function aiUsageLoad() {
  try {
    return JSON.parse(localStorage.getItem(AI_USAGE_KEY)) || { history: [], totalIn: 0, totalOut: 0, totalCost: 0, totalCalls: 0 }
  } catch (_) {
    return { history: [], totalIn: 0, totalOut: 0, totalCost: 0, totalCalls: 0 }
  }
}

function aiUsageSave(usage) {
  try { localStorage.setItem(AI_USAGE_KEY, JSON.stringify(usage)) } catch (_) {}
}

function aiUsageEstimateCost(model, inputTokens, outputTokens) {
  const pricing = AI_PRICING[model] || AI_PRICING['claude-sonnet-4-5']
  return ((inputTokens / 1_000_000) * pricing.in) + ((outputTokens / 1_000_000) * pricing.out)
}

function aiUsageRecord(model, usage, action) {
  if (!usage) return undefined
  const inputTokens = usage.input_tokens || usage.inputTokens || 0
  const outputTokens = usage.output_tokens || usage.outputTokens || 0
  const cost = aiUsageEstimateCost(model, inputTokens, outputTokens)
  const current = aiUsageLoad()
  current.history.unshift({ at: new Date().toISOString(), model, action, inputTokens, outputTokens, cost })
  current.history = current.history.slice(0, 500)
  current.totalIn += inputTokens
  current.totalOut += outputTokens
  current.totalCost += cost
  current.totalCalls += 1
  aiUsageSave(current)
  return { inputTokens, outputTokens, cost }
}

function aiUsageReset() {
  if (!confirm('Reset contador?')) return false
  aiUsageSave({ history: [], totalIn: 0, totalOut: 0, totalCost: 0, totalCalls: 0 })
  return true
}

export {
  AI_AGENT_KEYS,
  AI_CONFIG_KEY,
  AI_AGENT_PROMPT_URLS,
  AI_AGENT_DEFAULTS,
  AI_PRICING,
  AI_USAGE_KEY,
  aiLoadAgentDefault,
  aiLoadAgentPrompt,
  aiSaveAgentPrompt,
  aiResolveAgentPrompt,
  aiLoadKnowledgeBase,
  aiCallAgent,
  aiCallImporterAgent,
  aiCallAgentDashboard,
  aiCallDashboardImporterAgent,
  biaGenerateDashboardImporterAssistant,
  aiUsageLoad,
  aiUsageSave,
  aiUsageEstimateCost,
  aiUsageRecord,
  aiSetCostContext,
  aiCostContext,
  aiUsageReset,
}
